import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Package, Users, Download, Search, LogOut,
  RefreshCw, ArrowLeft, BarChart3, Lock, Eye, EyeOff,
  X, Edit3, Ban, SkipForward, ChevronRight, Save, AlertTriangle,
  CreditCard, Truck, ShoppingCart, Trash2, Clock, Mail, Send, Phone, Key, RotateCcw, Check
} from 'lucide-react';

import { API, authOpts, getAuthHeaders } from '../config/api';

function StatusBadge({ status }) {
  const colors = {
    paid: 'bg-green-100 text-green-800',
    active: 'bg-green-100 text-green-800',
    confirmed_ramburs: 'bg-amber-100 text-amber-800',
    pending_payment: 'bg-yellow-100 text-yellow-800',
    pending: 'bg-yellow-100 text-yellow-800',
    cancelled: 'bg-red-100 text-red-800',
    deleted: 'bg-red-200 text-red-900',
    paused: 'bg-blue-100 text-blue-800',
    shipped: 'bg-indigo-100 text-indigo-800',
    fulfilled: 'bg-teal-100 text-teal-800',
  };
  return (
    <span data-testid={`status-badge-${status}`} className={`px-2 py-1 rounded-full text-xs font-semibold ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
      {status?.replace('_', ' ').toUpperCase()}
    </span>
  );
}

/* ==================== ORDER DETAIL MODAL ==================== */
function OrderDetailModal({ order, onClose, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [status, setStatus] = useState(order.status || '');
  const [notes, setNotes] = useState(order.notes || '');
  const [trackingNumber, setTrackingNumber] = useState(order.tracking_number || order.tracking_code || '');
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sendingTracking, setSendingTracking] = useState(false);
  const [editingUser, setEditingUser] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  const [userSaveMsg, setUserSaveMsg] = useState('');
  const [editFields, setEditFields] = useState({
    customer_name: '', email: '', phone: '', address: '',
    city: '', county: '', zip_code: '', pet_name: '', new_password: '',
  });

  const handleSaveUser = async () => {
    setSavingUser(true);
    setUserSaveMsg('');
    try {
      const res = await fetch(`${API}/admin/orders/${order.order_id}/edit-user`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editFields),
      });
      if (res.ok) {
        const result = await res.json();
        let msg = 'Saved!';
        if (result.email_resent) msg += ' Welcome email resent to new address.';
        if (editFields.new_password) msg += ' Password updated.';
        setUserSaveMsg(msg);
        setEditingUser(false);
        onRefresh();
      } else {
        setUserSaveMsg('Error saving changes');
      }
    } catch (err) {
      setUserSaveMsg('Error: ' + err.message);
    } finally {
      setSavingUser(false);
    }
  };

  const handleSendTracking = async () => {
    if (!trackingNumber.trim()) return;
    setSendingTracking(true);
    try {
      const res = await fetch(`${API}/admin/orders/${order.order_id}/tracking`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tracking_code: trackingNumber.trim() }),
      });
      if (res.ok) {
        alert('Tracking saved & email sent to customer!');
        onRefresh();
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed');
      }
    } catch (err) {
      console.error('Tracking error:', err);
    } finally {
      setSendingTracking(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/orders/${order.order_id}`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status, notes, tracking_number: trackingNumber }),
      });
      if (res.ok) {
        setEditing(false);
        onRefresh();
      }
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this order? This will also cancel the related subscription.')) return;
    setCancelling(true);
    try {
      const res = await fetch(`${API}/admin/orders/${order.order_id}/cancel`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        onRefresh();
        onClose();
      }
    } catch (err) {
      console.error('Cancel error:', err);
    } finally {
      setCancelling(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('PERMANENTLY DELETE this order and all related data? This cannot be undone!')) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API}/admin/orders/${order.order_id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        onRefresh();
        onClose();
      }
    } catch (err) {
      console.error('Delete error:', err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto shadow-2xl" data-testid="order-detail-modal">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Order Details</h2>
            <p className="text-sm text-gray-500 font-mono">{order.order_id}</p>
          </div>
          <div className="flex items-center gap-2">
            {!editing && (
              <button onClick={() => setEditing(true)} className="p-2 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-blue-50" data-testid="edit-order-btn">
                <Edit3 className="w-4 h-4" />
              </button>
            )}
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600" data-testid="close-order-modal">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Status + Actions */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">Status:</span>
              {editing ? (
                <select value={status} onChange={(e) => setStatus(e.target.value)}
                  className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm" data-testid="edit-status-select">
                  {['pending_payment', 'paid', 'shipped', 'fulfilled', 'cancelled'].map(s => (
                    <option key={s} value={s}>{s.replace('_', ' ').toUpperCase()}</option>
                  ))}
                </select>
              ) : (
                <StatusBadge status={order.status} />
              )}
            </div>
            {order.status !== 'cancelled' && (
              <button onClick={handleCancel} disabled={cancelling}
                className="flex items-center gap-1.5 px-3 py-1.5 text-red-600 border border-red-200 rounded-lg text-sm hover:bg-red-50 disabled:opacity-50"
                data-testid="cancel-order-btn">
                <Ban className="w-3.5 h-3.5" />
                {cancelling ? 'Cancelling...' : 'Cancel Order'}
              </button>
            )}
            <button onClick={handleDelete} disabled={deleting}
              className="flex items-center gap-1.5 px-3 py-1.5 text-red-800 border border-red-300 rounded-lg text-sm hover:bg-red-100 disabled:opacity-50"
              data-testid="delete-order-btn">
              <AlertTriangle className="w-3.5 h-3.5" />
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>

          {/* Customer Info — Editable */}
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700">Customer</h3>
              {!editingUser ? (
                <button onClick={() => { setEditingUser(true); setEditFields({
                  customer_name: order.customer_name || '',
                  email: order.email || '',
                  phone: order.phone || '',
                  address: order.address || '',
                  city: order.city || '',
                  county: order.county || '',
                  zip_code: order.zip_code || '',
                  pet_name: order.pet_name || '',
                  new_password: '',
                }); }} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800" data-testid="edit-user-btn">
                  <Edit3 className="w-3 h-3" /> Edit Info
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <button onClick={handleSaveUser} disabled={savingUser}
                    className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 disabled:opacity-50"
                    data-testid="save-user-btn">
                    <Save className="w-3 h-3" /> {savingUser ? 'Saving...' : 'Save'}
                  </button>
                  <button onClick={() => setEditingUser(false)} className="px-3 py-1 text-gray-500 border rounded-lg text-xs hover:bg-gray-100">Cancel</button>
                </div>
              )}
            </div>
            {!editingUser ? (
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-gray-500">Name:</span> <span className="font-medium">{order.customer_name}</span></div>
                <div><span className="text-gray-500">Email:</span> <span className="font-medium">{order.email}</span></div>
                <div><span className="text-gray-500">Phone:</span> <span className="font-medium">{order.phone}</span></div>
                <div><span className="text-gray-500">City:</span> <span className="font-medium">{order.city || '-'}</span></div>
                <div><span className="text-gray-500">County:</span> <span className="font-medium">{order.county || '-'}</span></div>
                <div><span className="text-gray-500">Zip:</span> <span className="font-medium">{order.zip_code || '-'}</span></div>
                <div className="col-span-2"><span className="text-gray-500">Address:</span> <span className="font-medium">{order.address}</span></div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <label className="text-gray-500 text-xs">Name</label>
                  <input type="text" value={editFields.customer_name} onChange={e => setEditFields(p => ({...p, customer_name: e.target.value}))}
                    className="w-full px-3 py-1.5 rounded-lg border border-gray-200 text-sm mt-0.5" data-testid="edit-name" />
                </div>
                <div>
                  <label className="text-gray-500 text-xs">Email</label>
                  <input type="email" value={editFields.email} onChange={e => setEditFields(p => ({...p, email: e.target.value}))}
                    className="w-full px-3 py-1.5 rounded-lg border border-gray-200 text-sm mt-0.5" data-testid="edit-email" />
                  {editFields.email !== order.email && editFields.email && (
                    <p className="text-[10px] text-amber-600 mt-1 flex items-center gap-1"><Mail className="w-3 h-3" /> Welcome email will be resent. Set a new password below to include it in the email.</p>
                  )}
                </div>
                <div>
                  <label className="text-gray-500 text-xs">Phone</label>
                  <input type="text" value={editFields.phone} onChange={e => setEditFields(p => ({...p, phone: e.target.value}))}
                    className="w-full px-3 py-1.5 rounded-lg border border-gray-200 text-sm mt-0.5" data-testid="edit-phone" />
                </div>
                <div>
                  <label className="text-gray-500 text-xs">City</label>
                  <input type="text" value={editFields.city} onChange={e => setEditFields(p => ({...p, city: e.target.value}))}
                    className="w-full px-3 py-1.5 rounded-lg border border-gray-200 text-sm mt-0.5" data-testid="edit-city" />
                </div>
                <div>
                  <label className="text-gray-500 text-xs">County</label>
                  <input type="text" value={editFields.county} onChange={e => setEditFields(p => ({...p, county: e.target.value}))}
                    className="w-full px-3 py-1.5 rounded-lg border border-gray-200 text-sm mt-0.5" data-testid="edit-county" />
                </div>
                <div>
                  <label className="text-gray-500 text-xs">Zip</label>
                  <input type="text" value={editFields.zip_code} onChange={e => setEditFields(p => ({...p, zip_code: e.target.value}))}
                    className="w-full px-3 py-1.5 rounded-lg border border-gray-200 text-sm mt-0.5" data-testid="edit-zip" />
                </div>
                <div className="col-span-2">
                  <label className="text-gray-500 text-xs">Address</label>
                  <input type="text" value={editFields.address} onChange={e => setEditFields(p => ({...p, address: e.target.value}))}
                    className="w-full px-3 py-1.5 rounded-lg border border-gray-200 text-sm mt-0.5" data-testid="edit-address" />
                </div>
                <div>
                  <label className="text-gray-500 text-xs">Pet Name</label>
                  <input type="text" value={editFields.pet_name} onChange={e => setEditFields(p => ({...p, pet_name: e.target.value}))}
                    className="w-full px-3 py-1.5 rounded-lg border border-gray-200 text-sm mt-0.5" data-testid="edit-pet-name" />
                </div>
                <div>
                  <label className="text-gray-500 text-xs flex items-center gap-1"><Key className="w-3 h-3" /> New Password</label>
                  <input type="text" value={editFields.new_password} onChange={e => setEditFields(p => ({...p, new_password: e.target.value}))}
                    className="w-full px-3 py-1.5 rounded-lg border border-gray-200 text-sm mt-0.5" placeholder="Leave empty to keep current"
                    data-testid="edit-password" />
                </div>
                {userSaveMsg && <p className={`col-span-2 text-xs ${userSaveMsg.includes('Error') ? 'text-red-500' : 'text-green-600'}`}>{userSaveMsg}</p>}
              </div>
            )}
          </div>

          {/* Pet Info */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Pet Details</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-gray-500">Name:</span> <span className="font-medium">{order.pet_name}</span></div>
              <div><span className="text-gray-500">Type:</span> <span className="font-medium capitalize">{order.pet_type}</span></div>
              <div><span className="text-gray-500">Gender:</span> <span className="font-medium capitalize">{order.pet_gender}</span></div>
              <div><span className="text-gray-500">Breed:</span> <span className="font-medium">{order.breed || '-'}</span></div>
              <div><span className="text-gray-500">Age:</span> <span className="font-medium">{order.age || '-'}</span></div>
              {order.dog_size && <div><span className="text-gray-500">Size:</span> <span className="font-medium capitalize">{order.dog_size}</span></div>}
              <div><span className="text-gray-500">Allergies:</span> <span className="font-medium">{(order.allergies || []).join(', ') || 'None'}</span></div>
              <div><span className="text-gray-500">Upgrade:</span> <span className="font-medium">{order.wants_upgrade ? 'Yes' : 'No'}</span></div>
            </div>
          </div>

          {/* Payment */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Payment</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-gray-500">Method:</span> <span className="font-medium capitalize">{order.payment_method === 'ramburs' ? 'COD (Ramburs)' : 'Card (Stripe)'}</span></div>
              <div><span className="text-gray-500">Base:</span> <span className="font-medium">{order.currency === 'RON' ? '' : '\u20AC'}{order.base_price?.toFixed(2)}{order.currency === 'RON' ? ' lei' : ''}</span></div>
              <div><span className="text-gray-500">Upgrade:</span> <span className="font-medium">{order.currency === 'RON' ? '' : '\u20AC'}{order.upgrade_price?.toFixed(2)}{order.currency === 'RON' ? ' lei' : ''}</span></div>
              <div><span className="text-gray-500">Total:</span> <span className="font-bold text-brand-orange">{order.currency === 'RON' ? '' : '\u20AC'}{order.total_price?.toFixed(2)}{order.currency === 'RON' ? ' lei' : ''}/month</span></div>
              <div><span className="text-gray-500">Date:</span> <span className="font-medium">{order.created_at ? new Date(order.created_at).toLocaleString() : '-'}</span></div>
            </div>
          </div>

          {/* Tracking Code — always visible */}
          <div className="bg-indigo-50 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Tracking</h3>
            <div className="flex gap-2">
              <input
                type="text"
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                placeholder="Enter DPD tracking code..."
                className="flex-1 px-4 py-2 rounded-lg border border-gray-200 text-sm"
                data-testid="tracking-input"
              />
              <button
                onClick={handleSendTracking}
                disabled={!trackingNumber.trim() || sendingTracking}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 whitespace-nowrap"
                data-testid="send-tracking-btn"
              >
                {sendingTracking ? 'Sending...' : order.tracking_code ? 'Update & Notify' : 'Save & Notify'}
              </button>
            </div>
            {order.tracking_code && (
              <p className="text-xs text-indigo-600 mt-2">
                Current: <span className="font-mono font-bold">{order.tracking_code}</span>
                {order.shipped_at && <span className="text-gray-400 ml-2">shipped {new Date(order.shipped_at).toLocaleDateString()}</span>}
              </p>
            )}
          </div>

          {/* Tracking & Notes (editable) */}
          {editing && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tracking Number</label>
                <input type="text" value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border border-gray-200 text-sm" placeholder="Enter tracking number..."
                  data-testid="tracking-number-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Admin Notes</label>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border border-gray-200 text-sm resize-none" rows={3} placeholder="Internal notes..."
                  data-testid="admin-notes-input" />
              </div>
              <div className="flex gap-3">
                <button onClick={handleSave} disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                  data-testid="save-order-btn">
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                <button onClick={() => setEditing(false)} className="px-4 py-2 text-gray-600 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Display notes/tracking if not editing */}
          {!editing && (order.tracking_number || order.notes) && (
            <div className="bg-blue-50 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-blue-700 mb-2">Notes & Tracking</h3>
              {order.tracking_number && <p className="text-sm"><span className="text-blue-600">Tracking:</span> {order.tracking_number}</p>}
              {order.notes && <p className="text-sm mt-1"><span className="text-blue-600">Notes:</span> {order.notes}</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ==================== SUBSCRIPTION DETAIL MODAL ==================== */
function SubDetailModal({ sub, onClose, onRefresh, onViewOrder }) {
  const [cancelling, setCancelling] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this subscription?')) return;
    setCancelling(true);
    try {
      const res = await fetch(`${API}/admin/subscriptions/${sub.subscription_id}/cancel`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        onRefresh();
        onClose();
      }
    } catch (err) {
      console.error('Cancel error:', err);
    } finally {
      setCancelling(false);
    }
  };

  const handleSkip = async () => {
    if (!window.confirm('Skip the next month for this subscription?')) return;
    setSkipping(true);
    try {
      const res = await fetch(`${API}/admin/subscriptions/${sub.subscription_id}/skip`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        alert(`Skipped: ${data.message}`);
        onRefresh();
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to skip month');
      }
    } catch (err) {
      console.error('Skip error:', err);
    } finally {
      setSkipping(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this subscription? A cancellation email will be sent to the customer.')) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API}/admin/subscriptions/${sub.subscription_id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        onRefresh();
        onClose();
      }
    } catch (err) {
      console.error('Delete error:', err);
    } finally {
      setDeleting(false);
    }
  };

  const handleRestore = async () => {
    if (!window.confirm('Restore this subscription? It will be reactivated (including Stripe if applicable).')) return;
    setRestoring(true);
    try {
      const res = await fetch(`${API}/admin/subscriptions/${sub.subscription_id}/restore`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        alert(data.stripe_reactivated 
          ? 'Subscription restored & Stripe reactivated!' 
          : 'Subscription restored! (No Stripe subscription to reactivate)');
        onRefresh();
        onClose();
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to restore subscription');
      }
    } catch (err) {
      console.error('Restore error:', err);
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto shadow-2xl" data-testid="sub-detail-modal">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Subscription Details</h2>
            <p className="text-sm text-gray-500 font-mono">{sub.subscription_id}</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600" data-testid="close-sub-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Status + Actions */}
          <div className="flex flex-wrap items-center gap-3">
            <StatusBadge status={sub.status} />
            {(sub.status === 'deleted' || sub.status === 'cancelled') && (
              <button onClick={handleRestore} disabled={restoring}
                className="flex items-center gap-1.5 px-3 py-1.5 text-green-700 bg-green-50 border border-green-200 rounded-lg text-sm hover:bg-green-100 disabled:opacity-50"
                data-testid="restore-sub-btn">
                <RotateCcw className="w-3.5 h-3.5" />
                {restoring ? 'Restoring...' : 'Restore Subscription'}
              </button>
            )}
            {sub.status === 'active' && (
              <>
                <button onClick={handleSkip} disabled={skipping}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-blue-600 border border-blue-200 rounded-lg text-sm hover:bg-blue-50 disabled:opacity-50"
                  data-testid="skip-month-btn">
                  <SkipForward className="w-3.5 h-3.5" />
                  {skipping ? 'Skipping...' : 'Skip Next Month'}
                </button>
                <button onClick={handleCancel} disabled={cancelling}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-red-600 border border-red-200 rounded-lg text-sm hover:bg-red-50 disabled:opacity-50"
                  data-testid="cancel-sub-btn">
                  <Ban className="w-3.5 h-3.5" />
                  {cancelling ? 'Cancelling...' : 'Cancel Subscription'}
                </button>
              </>
            )}
            {sub.order_id && (
              <button onClick={() => { onClose(); onViewOrder(sub.order_id); }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-brand-orange border border-brand-orange/30 rounded-lg text-sm hover:bg-brand-orange/5"
                data-testid="view-linked-order-btn">
                <Edit3 className="w-3.5 h-3.5" />
                View / Edit Order
              </button>
            )}
            {sub.status !== 'deleted' && (
              <button onClick={handleDelete} disabled={deleting}
                className="flex items-center gap-1.5 px-3 py-1.5 text-red-600 bg-red-50 border border-red-200 rounded-lg text-sm hover:bg-red-100 disabled:opacity-50"
                data-testid="delete-sub-btn">
                <X className="w-3.5 h-3.5" />
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            )}
          </div>

          {/* Subscription Info */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Subscription Info</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-gray-500">Plan:</span> <span className="font-medium capitalize">{sub.plan_type}</span></div>
              <div><span className="text-gray-500">Price:</span> <span className="font-bold text-brand-orange">{sub.currency === 'RON' ? '' : '\u20AC'}{sub.price?.toFixed(2)}{sub.currency === 'RON' ? ' lei' : ''}/month</span></div>
              <div><span className="text-gray-500">Start:</span> <span className="font-medium">{sub.start_date ? new Date(sub.start_date).toLocaleDateString() : '-'}</span></div>
              <div><span className="text-gray-500">Next Billing:</span> <span className="font-medium">{sub.next_billing_date ? new Date(sub.next_billing_date).toLocaleDateString() : sub.end_date ? new Date(sub.end_date).toLocaleDateString() : '-'}</span></div>
              <div><span className="text-gray-500">Upgrade:</span> <span className="font-medium">{sub.has_upgrade || sub.wants_upgrade ? 'Yes' : 'No'}</span></div>
              <div><span className="text-gray-500">Free Delivery:</span> <span className="font-medium">{sub.free_delivery ? 'Yes' : 'No'}</span></div>
            </div>
          </div>

          {/* Customer & Address */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Customer & Delivery</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-gray-500">Name:</span> <span className="font-medium">{sub.customer_name || sub.user_name || '-'}</span></div>
              <div><span className="text-gray-500">Email:</span> <span className="font-medium">{sub.email || sub.user_email || '-'}</span></div>
              <div><span className="text-gray-500">Phone:</span> <span className="font-medium">{sub.phone || '-'}</span></div>
              <div className="col-span-2"><span className="text-gray-500">Address:</span> <span className="font-medium">{sub.address || '-'}</span></div>
            </div>
          </div>

          {/* Pet Details */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Pet Details</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-gray-500">Name:</span> <span className="font-medium">{sub.pet_name || '-'}</span></div>
              <div><span className="text-gray-500">Type:</span> <span className="font-medium capitalize">{sub.pet_type}</span></div>
              {sub.pet_gender && <div><span className="text-gray-500">Gender:</span> <span className="font-medium capitalize">{sub.pet_gender}</span></div>}
              {sub.breed && <div><span className="text-gray-500">Breed:</span> <span className="font-medium">{sub.breed}</span></div>}
              {sub.age && <div><span className="text-gray-500">Age:</span> <span className="font-medium">{sub.age}</span></div>}
              {sub.dog_size && <div><span className="text-gray-500">Size:</span> <span className="font-medium capitalize">{sub.dog_size}</span></div>}
              {sub.allergies && sub.allergies.length > 0 && <div className="col-span-2"><span className="text-gray-500">Allergies:</span> <span className="font-medium">{sub.allergies.join(', ')}{sub.allergy_other ? ` (${sub.allergy_other})` : ''}</span></div>}
            </div>
          </div>

          {/* Skipped Months */}
          {sub.skipped_months && sub.skipped_months.length > 0 && (
            <div className="bg-yellow-50 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-yellow-700 mb-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> Skipped Months
              </h3>
              <div className="flex flex-wrap gap-2">
                {sub.skipped_months.map(m => (
                  <span key={m} className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">{m}</span>
                ))}
              </div>
            </div>
          )}

          {/* Stripe IDs */}
          {(sub.stripe_subscription_id || sub.stripe_customer_id) && (
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Stripe</h3>
              <div className="text-xs text-gray-500 space-y-1 font-mono">
                {sub.stripe_subscription_id && <p>Sub: {sub.stripe_subscription_id}</p>}
                {sub.stripe_customer_id && <p>Customer: {sub.stripe_customer_id}</p>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ==================== CREATE SUBSCRIPTION MODAL ==================== */
function CreateSubModal({ onClose, onRefresh }) {
  const [form, setForm] = useState({
    customer_name: '', email: '', phone: '', address: '', city: '',
    pet_name: '', pet_type: 'dog', dog_size: 'medium', wants_upgrade: false,
    currency: 'RON', price: 99,
  });
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);

  const update = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleCreate = async () => {
    if (!form.email || !form.customer_name) return alert('Name and email are required');
    setCreating(true);
    try {
      const res = await fetch(`${API}/admin/subscriptions/create`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok) {
        setResult(data);
      } else {
        alert(data.detail || 'Failed to create subscription');
      }
    } catch (err) {
      console.error('Create sub error:', err);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto shadow-2xl" data-testid="create-sub-modal">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-lg font-bold text-gray-900">Create Subscription</h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        {result ? (
          <div className="p-6 space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
              <Check className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="font-bold text-green-800">Subscription Created!</p>
              <p className="text-sm text-green-700 mt-1">Welcome email sent to {result.email}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 text-sm space-y-2">
              <div><span className="text-gray-500">Sub ID:</span> <span className="font-mono font-medium">{result.subscription_id}</span></div>
              <div><span className="text-gray-500">Order ID:</span> <span className="font-mono font-medium">{result.order_id}</span></div>
              <div><span className="text-gray-500">Email:</span> <span className="font-medium">{result.email}</span></div>
              <div><span className="text-gray-500">Password:</span> <span className="font-mono font-bold text-brand-orange">{result.password}</span></div>
            </div>
            <button onClick={() => { onRefresh(); onClose(); }}
              className="w-full py-2.5 bg-brand-orange text-white rounded-lg font-medium hover:bg-brand-orange/90"
              data-testid="create-sub-done-btn">Done</button>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-xs font-medium text-gray-500 mb-1 block">Customer Name *</label>
                <input value={form.customer_name} onChange={e => update('customer_name', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-brand-orange" data-testid="create-sub-name" />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-gray-500 mb-1 block">Email *</label>
                <input type="email" value={form.email} onChange={e => update('email', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-brand-orange" data-testid="create-sub-email" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Phone</label>
                <input value={form.phone} onChange={e => update('phone', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-brand-orange" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">City</label>
                <input value={form.city} onChange={e => update('city', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-brand-orange" />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-gray-500 mb-1 block">Address</label>
                <input value={form.address} onChange={e => update('address', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-brand-orange" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Pet Name</label>
                <input value={form.pet_name} onChange={e => update('pet_name', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-brand-orange" data-testid="create-sub-petname" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Pet Type</label>
                <select value={form.pet_type} onChange={e => update('pet_type', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm">
                  <option value="dog">Dog</option>
                  <option value="cat">Cat</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Dog Size</label>
                <select value={form.dog_size} onChange={e => update('dog_size', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm">
                  <option value="small">Small</option>
                  <option value="medium">Medium</option>
                  <option value="large">Large</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Currency</label>
                <select value={form.currency} onChange={e => { update('currency', e.target.value); update('price', e.target.value === 'RON' ? 99 : 24); }}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm">
                  <option value="RON">RON (lei)</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Price / month</label>
                <input type="number" value={form.price} onChange={e => update('price', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-brand-orange" />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={form.wants_upgrade} onChange={e => update('wants_upgrade', e.target.checked)} id="upgrade-check" />
                <label htmlFor="upgrade-check" className="text-sm text-gray-700">Wants upgrade (+UP)</label>
              </div>
            </div>
            <button onClick={handleCreate} disabled={creating}
              className="w-full py-2.5 bg-brand-orange text-white rounded-lg font-medium hover:bg-brand-orange/90 disabled:opacity-50"
              data-testid="create-sub-submit-btn">
              {creating ? 'Creating...' : 'Create Subscription & Send Email'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ==================== ADMIN LOGIN ==================== */
function AdminLogin() {
  const { loginWithEmail } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const userData = await loginWithEmail(email, password);
      if (userData.role !== 'admin') {
        setError('Access denied. Admin only.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
          <div className="text-center mb-6">
            <div className="w-14 h-14 bg-brand-brown rounded-full flex items-center justify-center mx-auto mb-3">
              <Lock className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-xl font-bold text-gray-900">PetsBox Admin</h1>
            <p className="text-gray-500 text-sm mt-1">Sign in to manage orders</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Admin email"
                className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-brand-orange"
                required
                data-testid="admin-email-input"
              />
            </div>
            <div className="relative">
              <input
                type={showPass ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-brand-orange pr-10"
                required
                data-testid="admin-password-input"
              />
              <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {error && <p className="text-red-500 text-sm" data-testid="admin-login-error">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-brand-brown text-white py-3 rounded-lg font-medium hover:bg-brand-brown/90 transition-colors disabled:opacity-50"
              data-testid="admin-login-btn"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

/* ==================== MAIN ADMIN DASHBOARD ==================== */
function MonthlyBoxesPanel({ boxes, upcoming, month, onRefresh, orders, onSelectOrder }) {
  const [trackingInputs, setTrackingInputs] = useState({});
  const [sending, setSending] = useState({});

  const pending = boxes.filter(b => b.status === 'pending');
  const shipped = boxes.filter(b => b.status === 'shipped');

  const findOrder = (box) => {
    if (!orders) return null;
    return orders.find(o => o.order_id === box.order_id) || null;
  };

  const handleRowClick = (box) => {
    const order = findOrder(box);
    if (order && onSelectOrder) onSelectOrder(order);
  };

  const handleSendTracking = async (subId) => {
    const code = trackingInputs[subId]?.trim();
    if (!code) return;
    setSending(s => ({ ...s, [subId]: true }));
    try {
      const res = await fetch(`${API}/admin/monthly-boxes/${subId}/tracking`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tracking_code: code }),
      });
      if (res.ok) {
        setTrackingInputs(s => ({ ...s, [subId]: '' }));
        onRefresh();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSending(s => ({ ...s, [subId]: false }));
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="monthly-boxes-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Monthly Boxes — {month}</h2>
          <p className="text-sm text-gray-500">{pending.length} pending, {shipped.length} shipped</p>
        </div>
        <div className="flex gap-3">
          <div className="px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-xs font-bold">{pending.length} Pending</div>
          <div className="px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-xs font-bold">{shipped.length} Shipped</div>
        </div>
      </div>

      {/* Pending boxes */}
      {pending.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <span className="w-2 h-2 bg-amber-500 rounded-full" /> Pending Shipments
          </h3>
          <div className="space-y-3">
            {pending.map(box => (
              <div key={box.subscription_id} className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col lg:flex-row lg:items-center gap-3"
                data-testid={`monthly-box-${box.subscription_id}`}>
                <div className="flex-1 grid grid-cols-2 lg:grid-cols-4 gap-2 text-sm cursor-pointer hover:bg-gray-50 rounded-lg p-1 transition-colors"
                  onClick={() => handleRowClick(box)}
                  title="Click to view order details">
                  <div>
                    <p className="text-gray-400 text-xs">Customer</p>
                    <p className="font-semibold">{box.customer_name || '-'}</p>
                    <p className="text-xs text-gray-500">{box.email}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs">Pet</p>
                    <p className="font-semibold">{box.pet_name || '-'}</p>
                    <p className="text-xs text-gray-500 capitalize">{box.pet_type}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs">Box Day</p>
                    <p className="font-semibold">Due: {box.box_day}{box.box_day === 1 ? 'st' : box.box_day === 2 ? 'nd' : box.box_day === 3 ? 'rd' : 'th'} of each month</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs">Payment</p>
                    <p className="font-semibold capitalize">{box.payment_method === 'ramburs' ? 'COD' : 'Card'}</p>
                  </div>
                </div>
                <div className="flex gap-2 lg:w-80">
                  <input
                    type="text"
                    value={trackingInputs[box.subscription_id] || ''}
                    onChange={(e) => setTrackingInputs(s => ({ ...s, [box.subscription_id]: e.target.value }))}
                    placeholder="DPD tracking..."
                    className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm"
                  />
                  <button
                    onClick={() => handleSendTracking(box.subscription_id)}
                    disabled={!trackingInputs[box.subscription_id]?.trim() || sending[box.subscription_id]}
                    className="px-4 py-2 bg-brand-orange text-white rounded-lg text-sm font-semibold hover:bg-brand-orange-hover disabled:opacity-50 whitespace-nowrap"
                  >
                    {sending[box.subscription_id] ? '...' : 'Ship & Notify'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Shipped boxes */}
      {shipped.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <span className="w-2 h-2 bg-green-500 rounded-full" /> Shipped This Month
          </h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-100">
                {['Customer', 'Pet', 'Tracking', 'Shipped', 'Payment'].map(h => <th key={h} className="px-4 py-2 font-medium">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {shipped.map(box => (
                <tr key={box.subscription_id} className="border-b border-gray-50 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => handleRowClick(box)}
                  title="Click to view order details">
                  <td className="px-4 py-2">
                    <div className="font-medium">{box.customer_name || '-'}</div>
                    <div className="text-xs text-gray-500">{box.email}</div>
                  </td>
                  <td className="px-4 py-2">{box.pet_name} <span className="text-xs text-gray-400 capitalize">({box.pet_type})</span></td>
                  <td className="px-4 py-2">
                    <a href={`https://www.speedy.bg/public/index.php/en/track-shipment?shipmentNumber=${box.tracking_code}`} target="_blank" rel="noopener noreferrer"
                      className="font-mono text-indigo-600 hover:underline font-bold">{box.tracking_code}</a>
                  </td>
                  <td className="px-4 py-2 text-gray-500 text-xs">{box.shipped_at ? new Date(box.shipped_at).toLocaleDateString() : '-'}</td>
                  <td className="px-4 py-2 capitalize">{box.payment_method === 'ramburs' ? 'COD' : 'Card'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {boxes.length === 0 && (!upcoming || upcoming.length === 0) && (
        <p className="text-center text-gray-400 py-12">No active subscriptions for this month</p>
      )}

      {/* Upcoming boxes — not yet due */}
      {upcoming && upcoming.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-blue-400" /> Upcoming This Month ({upcoming.length})
            <span className="text-xs font-normal text-gray-400">— Not yet due</span>
          </h3>
          <div className="space-y-2">
            {upcoming.sort((a, b) => a.box_day - b.box_day).map(box => (
              <div key={box.subscription_id}
                className="flex items-center justify-between bg-blue-50 border border-blue-100 rounded-lg px-4 py-3 cursor-pointer hover:bg-blue-100 transition-colors"
                onClick={() => handleRowClick(box)}
                title="Click to view order details">
                <div className="flex items-center gap-6 flex-1">
                  <div className="text-center min-w-[50px]">
                    <div className="text-lg font-bold text-blue-600">{box.box_day}</div>
                    <div className="text-[10px] text-blue-400 uppercase">due</div>
                  </div>
                  <div className="min-w-[140px]">
                    <div className="font-medium text-gray-900 text-sm">{box.customer_name || '-'}</div>
                    <div className="text-xs text-gray-500">{box.email}</div>
                  </div>
                  <div className="text-sm text-gray-600">
                    {box.pet_name && <span className="mr-3">{box.pet_name}</span>}
                    <span className="text-xs text-gray-400 capitalize">({box.pet_type})</span>
                  </div>
                </div>
                <span className="text-xs text-blue-500 font-medium px-2 py-1 bg-blue-100 rounded-full">
                  In {box.box_day - new Date().getDate()} days
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ==================== ABANDONED CARTS PANEL ==================== */
function AbandonedCartsPanel({ carts, onRefresh }) {
  const [deleting, setDeleting] = useState(null);
  const [resending, setResending] = useState(null);

  const handleDelete = async (cartId) => {
    setDeleting(cartId);
    try {
      const res = await fetch(`${API}/admin/abandoned-carts/${cartId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (res.ok) onRefresh();
    } catch (err) {
      console.error('Delete error:', err);
    } finally {
      setDeleting(null);
    }
  };

  const handleResend = async (cartId) => {
    setResending(cartId);
    try {
      const res = await fetch(`${API}/admin/abandoned-carts/${cartId}/resend`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        onRefresh();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Failed to resend');
      }
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setResending(null);
    }
  };

  const pending = carts.filter(c => !c.reminder_sent);
  const reminded = carts.filter(c => c.reminder_sent);

  const timeAgo = (dateStr) => {
    if (!dateStr) return '-';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  return (
    <div className="p-4 space-y-6" data-testid="abandoned-carts-panel">
      {/* Pending - not yet reminded */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <ShoppingCart className="w-4 h-4 text-amber-500" />
          Pending ({pending.length})
          <span className="text-xs font-normal text-gray-400">— Not yet sent reminder email</span>
        </h3>
        {pending.length === 0 ? (
          <p className="text-gray-400 text-sm py-4 text-center">No pending abandoned carts</p>
        ) : (
          <div className="space-y-2">
            {pending.map(cart => (
              <div key={cart.cart_id} className="flex items-center justify-between bg-amber-50 border border-amber-100 rounded-lg px-4 py-3" data-testid={`cart-${cart.cart_id}`}>
                <div className="flex items-center gap-6 flex-1">
                  <div className="min-w-[180px]">
                    <div className="font-medium text-gray-900 text-sm">{cart.email}</div>
                    <div className="text-xs text-gray-500">{cart.customer_name || '-'}</div>
                  </div>
                  <div className="text-sm text-gray-600">
                    {cart.pet_name && <span className="mr-3">Pet: <strong>{cart.pet_name}</strong></span>}
                    {cart.pet_type && <span className="mr-3 capitalize">{cart.pet_type}</span>}
                    {cart.phone && <span className="text-gray-500 flex items-center gap-1"><Phone className="w-3 h-3" />{cart.phone}</span>}
                  </div>
                  {cart.funnel_step && (
                    <span className="text-xs bg-white border px-2 py-0.5 rounded-full text-gray-500">Step {cart.funnel_step}</span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-400 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {timeAgo(cart.updated_at || cart.created_at)}
                  </span>
                  <button onClick={() => handleResend(cart.cart_id)} disabled={resending === cart.cart_id}
                    className="flex items-center gap-1 px-2.5 py-1 text-xs bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 transition-colors"
                    data-testid={`resend-cart-${cart.cart_id}`}>
                    <Send className="w-3 h-3" /> {resending === cart.cart_id ? 'Sending...' : 'Send Reminder'}
                  </button>
                  <button onClick={() => handleDelete(cart.cart_id)} disabled={deleting === cart.cart_id}
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    data-testid={`delete-cart-${cart.cart_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reminded */}
      {reminded.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Mail className="w-4 h-4 text-green-500" />
            Reminder Sent ({reminded.length})
          </h3>
          <div className="space-y-2">
            {reminded.map(cart => (
              <div key={cart.cart_id} className="flex items-center justify-between bg-gray-50 border border-gray-100 rounded-lg px-4 py-3" data-testid={`cart-${cart.cart_id}`}>
                <div className="flex items-center gap-6 flex-1">
                  <div className="min-w-[180px]">
                    <div className="font-medium text-gray-700 text-sm">{cart.email}</div>
                    <div className="text-xs text-gray-400">{cart.customer_name || '-'}</div>
                  </div>
                  <div className="text-sm text-gray-500">
                    {cart.pet_name && <span className="mr-3">Pet: {cart.pet_name}</span>}
                    {cart.pet_type && <span className="mr-3 capitalize">{cart.pet_type}</span>}
                    {cart.phone && <span className="text-gray-400 flex items-center gap-1"><Phone className="w-3 h-3" />{cart.phone}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-green-500 font-medium flex items-center gap-1">
                    <Mail className="w-3 h-3" /> Emailed
                  </span>
                  <button onClick={() => handleResend(cart.cart_id)} disabled={resending === cart.cart_id}
                    className="flex items-center gap-1 px-2.5 py-1 text-xs bg-gray-400 text-white rounded-lg hover:bg-gray-500 disabled:opacity-50 transition-colors"
                    data-testid={`resend-reminded-${cart.cart_id}`}>
                    <Send className="w-3 h-3" /> {resending === cart.cart_id ? 'Sending...' : 'Resend'}
                  </button>
                  <button onClick={() => handleDelete(cart.cart_id)} disabled={deleting === cart.cart_id}
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    data-testid={`delete-cart-${cart.cart_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [stats, setStats] = useState({ totalOrders: 0, totalSubs: 0, activeSubs: 0, revenue: 0 });
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [exportType, setExportType] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedSub, setSelectedSub] = useState(null);
  const [monthlyBoxes, setMonthlyBoxes] = useState([]);
  const [upcomingBoxes, setUpcomingBoxes] = useState([]);
  const [monthlyMonth, setMonthlyMonth] = useState('');
  const [abandonedCarts, setAbandonedCarts] = useState([]);
  const [adminNotes, setAdminNotes] = useState('');
  const [notesSaved, setNotesSaved] = useState(true);
  const [savingNotes, setSavingNotes] = useState(false);
  const [showCreateSub, setShowCreateSub] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [duplicates, setDuplicates] = useState(null);
  const [loadingDupes, setLoadingDupes] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [ordersRes, subsRes, analyticsRes, boxesRes, cartsRes, notesRes] = await Promise.all([
        fetch(`${API}/admin/orders`, authOpts()),
        fetch(`${API}/admin/subscriptions`, authOpts()),
        fetch(`${API}/admin/analytics`, authOpts()),
        fetch(`${API}/admin/monthly-boxes`, authOpts()),
        fetch(`${API}/admin/abandoned-carts`, authOpts()),
        fetch(`${API}/admin/notes`, authOpts()),
      ]);
      
      const ordersData = ordersRes.ok ? await ordersRes.json() : { orders: [] };
      const subsData = subsRes.ok ? await subsRes.json() : { subscriptions: [] };
      if (analyticsRes.ok) setAnalytics(await analyticsRes.json());
      if (boxesRes.ok) {
        const boxesData = await boxesRes.json();
        setMonthlyBoxes(boxesData.boxes || []);
        setUpcomingBoxes(boxesData.upcoming || []);
        setMonthlyMonth(boxesData.month || '');
      }
      if (cartsRes.ok) {
        const cartsData = await cartsRes.json();
        setAbandonedCarts(cartsData.carts || []);
      }
      if (notesRes.ok) {
        const notesData = await notesRes.json();
        setAdminNotes(notesData.notes || '');
      }
      
      const oList = ordersData.orders || [];
      const sList = subsData.subscriptions || [];
      
      setOrders(oList);
      setSubscriptions(sList);
      const leiTotal = sList.filter(s => s.status === 'active' && (!s.currency || s.currency === 'RON')).reduce((sum, s) => sum + (s.price || 0), 0);
      const eurTotal = sList.filter(s => s.status === 'active' && s.currency && s.currency !== 'RON').reduce((sum, s) => sum + (s.price || 0), 0);
      setStats({
        totalOrders: oList.length,
        cardOrders: oList.filter(o => o.payment_method !== 'ramburs').length,
        codOrders: oList.filter(o => o.payment_method === 'ramburs').length,
        totalSubs: sList.length,
        activeSubs: sList.filter(s => s.status === 'active').length,
        revenueLei: leiTotal,
        revenueEur: eurTotal + Math.round(leiTotal / 5),
      });
    } catch (err) {
      console.error('Failed to fetch admin data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && user.role === 'admin') {
      fetchData();
    }
  }, [user, fetchData]);

  const filteredOrders = orders.filter(o => {
    const matchesSearch = !searchTerm || 
      [o.customer_name, o.email, o.pet_name, o.order_id].some(f => (f || '').toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || o.status === statusFilter;
    const matchesPayment = paymentFilter === 'all' || 
      (paymentFilter === 'cod' ? o.payment_method === 'ramburs' : o.payment_method !== 'ramburs');
    return matchesSearch && matchesStatus && matchesPayment;
  });

  const filteredSubs = subscriptions.filter(s => {
    const matchesSearch = !searchTerm ||
      [s.pet_name, s.user_email, s.user_name, s.subscription_id].some(f => (f || '').toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || s.status === statusFilter;
    return matchesSearch && matchesStatus && s.status !== 'deleted' && s.status !== 'cancelled';
  });

  const deletedSubs = subscriptions.filter(s => {
    const matchesSearch = !searchTerm ||
      [s.pet_name, s.user_email, s.user_name, s.subscription_id].some(f => (f || '').toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesSearch && (s.status === 'deleted' || s.status === 'cancelled');
  });

  const handleExportXLS = async () => {
    try {
      const res = await fetch(`${API}/admin/export/speedy?type=${exportType}`, authOpts());
      if (res.ok) {
        const blob = await res.blob();
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `petsbox_orders_${exportType}_${new Date().toISOString().split('T')[0]}.xls`;
        link.click();
      }
    } catch (err) {
      console.error('Export error:', err);
    }
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      const res = await fetch(`${API}/admin/notes`, {
        method: 'PUT',
        credentials: 'include',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: adminNotes }),
      });
      if (res.ok) setNotesSaved(true);
    } catch (err) {
      console.error('Save notes error:', err);
    } finally {
      setSavingNotes(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (ids) => {
    setSelectedIds(prev => {
      const allSelected = ids.every(id => prev.has(id));
      if (allSelected) return new Set();
      return new Set(ids);
    });
  };

  const handleBulkDelete = async () => {
    const count = selectedIds.size;
    if (!count) return;
    const isOrders = activeTab === 'orders';
    const label = isOrders ? 'orders' : 'subscriptions';
    if (!window.confirm(`Delete ${count} ${label}? ${!isOrders ? 'Cancellation emails will be sent.' : ''}`)) return;
    setBulkDeleting(true);
    try {
      const url = isOrders ? `${API}/admin/orders/bulk-delete` : `${API}/admin/subscriptions/bulk-delete`;
      const body = isOrders ? { order_ids: [...selectedIds] } : { subscription_ids: [...selectedIds] };
      const res = await fetch(url, {
        method: 'POST', credentials: 'include',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json();
        alert(data.message);
        setSelectedIds(new Set());
        fetchData();
      }
    } catch (err) {
      console.error('Bulk delete error:', err);
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleCheckDuplicates = async () => {
    setLoadingDupes(true);
    setShowDuplicates(true);
    try {
      const res = await fetch(`${API}/admin/duplicates`, authOpts());
      if (res.ok) setDuplicates(await res.json());
    } catch (err) {
      console.error('Duplicates error:', err);
    } finally {
      setLoadingDupes(false);
    }
  };

  // Show login if not admin
  if (!user || user.role !== 'admin') {
    return <AdminLogin />;
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      {/* Admin Header */}
      <header className="bg-brand-brown text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/')} className="p-2 hover:bg-white/10 rounded-lg" data-testid="admin-back">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold">PetsBox Admin</h1>
              <p className="text-white/60 text-sm">Order & Subscription Management</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-white/70">{user.email}</span>
            <button onClick={logout} className="p-2 hover:bg-white/10 rounded-lg" data-testid="admin-logout">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[
            { icon: Package, color: 'bg-brand-orange/10 text-brand-orange', value: stats.totalOrders, label: 'Total Orders' },
            { icon: CreditCard, color: 'bg-green-50 text-green-600', value: stats.cardOrders || 0, label: 'Card Orders' },
            { icon: Truck, color: 'bg-amber-50 text-amber-600', value: stats.codOrders || 0, label: 'COD Orders' },
            { icon: null, color: 'bg-purple-50 text-purple-600', value: null, label: 'Monthly Revenue', isRevenue: true },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100" data-testid={`stat-card-${i}`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${s.color.split(' ')[0]}`}>
                  {s.icon ? <s.icon className={`w-5 h-5 ${s.color.split(' ')[1]}`} /> : <span className={`text-lg font-bold ${s.color.split(' ')[1]}`}>{'\u20AC'}</span>}
                </div>
                <div>
                  {s.isRevenue ? (
                    <>
                      <p className="text-2xl font-bold text-gray-900">{stats.revenueEur?.toFixed(0) || 0}{'\u20AC'}</p>
                      <p className="text-xs text-gray-400">{stats.revenueLei?.toFixed(0) || 0} lei</p>
                    </>
                  ) : (
                    <p className="text-2xl font-bold text-gray-900">{s.value}</p>
                  )}
                  <p className="text-xs text-gray-500">{s.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Notepad */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6 p-4" data-testid="admin-notepad">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-600 flex items-center gap-2">
              <Edit3 className="w-4 h-4 text-gray-400" /> My Notes
            </h3>
            <button onClick={handleSaveNotes} disabled={savingNotes || notesSaved}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${notesSaved ? 'text-green-600 bg-green-50' : 'text-white bg-brand-orange hover:bg-brand-orange/90'}`}
              data-testid="save-notes-btn">
              {savingNotes ? <RefreshCw className="w-3 h-3 animate-spin" /> : notesSaved ? <Check className="w-3 h-3" /> : <Save className="w-3 h-3" />}
              {savingNotes ? 'Saving...' : notesSaved ? 'Saved' : 'Save'}
            </button>
          </div>
          <textarea
            value={adminNotes}
            onChange={(e) => { setAdminNotes(e.target.value); setNotesSaved(false); }}
            placeholder="Write your notes here..."
            className="w-full min-h-[60px] max-h-[200px] p-3 rounded-lg border border-gray-200 text-sm text-gray-700 resize-y focus:outline-none focus:border-brand-orange placeholder:text-gray-300"
            data-testid="admin-notes-textarea"
          />
        </div>

        {/* Tabs + Controls */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <div className="flex gap-1">
              {['orders', 'subscriptions', 'monthly', 'abandoned', 'deleted', 'analytics'].map(tab => (
                <button
                  key={tab}
                  onClick={() => { setActiveTab(tab); setStatusFilter('all'); setPaymentFilter('all'); setSelectedIds(new Set()); }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === tab ? 'bg-brand-orange text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                  data-testid={`tab-${tab}`}
                >
                  {tab === 'orders' ? <Package className="w-4 h-4 inline mr-1" /> : tab === 'subscriptions' ? <Users className="w-4 h-4 inline mr-1" /> : tab === 'monthly' ? <Truck className="w-4 h-4 inline mr-1" /> : tab === 'abandoned' ? <ShoppingCart className="w-4 h-4 inline mr-1" /> : tab === 'deleted' ? <Trash2 className="w-4 h-4 inline mr-1" /> : <BarChart3 className="w-4 h-4 inline mr-1" />}
                  {tab === 'monthly' ? 'Monthly Boxes' : tab === 'abandoned' ? 'Abandoned' : tab === 'deleted' ? 'Deleted / Cancelled' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                  {tab === 'monthly' && monthlyBoxes.filter(b => b.status === 'pending').length > 0 && (
                    <span className="ml-1 inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold bg-red-500 text-white rounded-full">
                      {monthlyBoxes.filter(b => b.status === 'pending').length}
                    </span>
                  )}
                  {tab === 'abandoned' && abandonedCarts.filter(c => !c.reminder_sent).length > 0 && (
                    <span className="ml-1 inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold bg-red-500 text-white rounded-full">
                      {abandonedCarts.filter(c => !c.reminder_sent).length}
                    </span>
                  )}
                  {tab === 'deleted' && subscriptions.filter(s => s.status === 'deleted' || s.status === 'cancelled').length > 0 && (
                    <span className="ml-1 inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold bg-gray-500 text-white rounded-full">
                      {subscriptions.filter(s => s.status === 'deleted' || s.status === 'cancelled').length}
                    </span>
                  )}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              {(activeTab === 'subscriptions' || activeTab === 'deleted') && (
                <button onClick={() => setShowCreateSub(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-orange text-white rounded-lg text-sm font-medium hover:bg-brand-orange/90"
                  data-testid="create-sub-btn">
                  <span className="text-lg leading-none">+</span> Create
                </button>
              )}
              <button onClick={handleCheckDuplicates}
                className="flex items-center gap-1.5 px-3 py-1.5 text-gray-600 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50"
                data-testid="check-dupes-btn">
                <Search className="w-3.5 h-3.5" /> Duplicates
              </button>
              {selectedIds.size > 0 && (
                <button onClick={handleBulkDelete} disabled={bulkDeleting}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
                  data-testid="bulk-delete-btn">
                  <Trash2 className="w-3.5 h-3.5" />
                  {bulkDeleting ? 'Deleting...' : `Delete ${selectedIds.size} selected`}
                </button>
              )}
              <button onClick={fetchData} className="p-2 text-gray-400 hover:text-brand-orange" data-testid="admin-refresh">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Search + Filter + Export */}
          <div className="flex flex-wrap items-center gap-3 p-4 border-b border-gray-50">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-brand-orange" data-testid="admin-search" />
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 rounded-lg border border-gray-200 text-sm" data-testid="admin-status-filter">
              <option value="all">All Status</option>
              {activeTab === 'orders'
                ? ['paid', 'confirmed_ramburs', 'pending_payment', 'shipped', 'fulfilled', 'cancelled'].map(s => <option key={s} value={s}>{s.replace('_', ' ').toUpperCase()}</option>)
                : ['active', 'paused', 'cancelled'].map(s => <option key={s} value={s}>{s.toUpperCase()}</option>)}
            </select>
            {activeTab === 'orders' && (
              <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5">
                {[
                  { key: 'all', label: 'All', icon: null },
                  { key: 'card', label: 'Card', icon: CreditCard },
                  { key: 'cod', label: 'COD', icon: Truck },
                ].map(f => (
                  <button
                    key={f.key}
                    onClick={() => setPaymentFilter(f.key)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      paymentFilter === f.key ? 'bg-white shadow-sm text-brand-brown' : 'text-gray-500 hover:text-gray-700'
                    }`}
                    data-testid={`payment-filter-${f.key}`}
                  >
                    {f.icon && <f.icon className="w-3 h-3" />}
                    {f.label}
                  </button>
                ))}
              </div>
            )}
            {activeTab === 'orders' && (
              <div className="flex items-center gap-2">
                <select value={exportType} onChange={(e) => setExportType(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-gray-200 text-sm" data-testid="export-type-select">
                  <option value="all">All This Month</option>
                  <option value="unfulfilled">Unfulfilled (First Box)</option>
                </select>
                <button onClick={handleExportXLS}
                  className="flex items-center gap-2 px-4 py-2 bg-brand-brown text-white rounded-lg text-sm font-medium hover:bg-brand-brown/90"
                  data-testid="export-xls-btn">
                  <Download className="w-4 h-4" /> Export XLS
                </button>
              </div>
            )}
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <RefreshCw className="w-8 h-8 text-brand-orange animate-spin" />
              </div>
            ) : activeTab === 'analytics' ? (
              <div className="p-6 space-y-6" data-testid="analytics-panel">
                {analytics ? (
                  <>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      {[
                        { label: 'Page Views Today', value: analytics.views.today, sub: `${analytics.views.week} this week` },
                        { label: 'Total Page Views', value: analytics.views.total, sub: `${analytics.views.month} this month` },
                        { label: 'Funnel Opens Today', value: analytics.funnels.today, sub: `${analytics.funnels.week} this week` },
                        { label: 'Total Funnel Opens', value: analytics.funnels.total, sub: `${analytics.funnels.month} this month` },
                      ].map((s, i) => (
                        <div key={i} className="bg-gray-50 rounded-xl p-4">
                          <p className="text-2xl font-bold text-gray-900">{s.value}</p>
                          <p className="text-sm font-medium text-gray-700">{s.label}</p>
                          <p className="text-xs text-gray-400 mt-1">{s.sub}</p>
                        </div>
                      ))}
                    </div>
                    
                    <div className="bg-gray-50 rounded-xl p-5">
                      <h3 className="text-sm font-semibold text-gray-700 mb-4">Views — Last 7 Days</h3>
                      <div className="flex items-end gap-2 h-32">
                        {analytics.daily_views.map((d, i) => {
                          const max = Math.max(...analytics.daily_views.map(x => x.views), 1);
                          const height = Math.max((d.views / max) * 100, 4);
                          return (
                            <div key={i} className="flex-1 flex flex-col items-center gap-1">
                              <span className="text-xs font-medium text-gray-600">{d.views}</span>
                              <div className="w-full bg-brand-orange/80 rounded-t-md" style={{height: `${height}%`}} />
                              <span className="text-[10px] text-gray-400">{d.date}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-gray-50 rounded-xl p-4 text-center">
                        <p className="text-xl font-bold text-brand-orange">{analytics.orders.today}</p>
                        <p className="text-xs text-gray-500">Orders Today</p>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-4 text-center">
                        <p className="text-xl font-bold text-brand-orange">{analytics.orders.week}</p>
                        <p className="text-xs text-gray-500">Orders This Week</p>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-4 text-center">
                        <p className="text-xl font-bold text-brand-orange">{analytics.orders.month}</p>
                        <p className="text-xs text-gray-500">Orders This Month</p>
                      </div>
                    </div>

                    {analytics.funnels.total > 0 && analytics.views.total > 0 && (
                      <div className="bg-gray-50 rounded-xl p-4">
                        <h3 className="text-sm font-semibold text-gray-700 mb-2">Conversion</h3>
                        <div className="flex items-center gap-8 text-sm">
                          <div><span className="text-gray-500">Visitors → Funnel:</span> <span className="font-bold">{((analytics.funnels.total / analytics.views.total) * 100).toFixed(1)}%</span></div>
                          <div><span className="text-gray-500">Funnel → Order:</span> <span className="font-bold">{analytics.funnels.total > 0 ? ((stats.totalOrders / analytics.funnels.total) * 100).toFixed(1) : 0}%</span></div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-center text-gray-400 py-12">Loading analytics...</p>
                )}
              </div>
            ) : activeTab === 'monthly' ? (
              <MonthlyBoxesPanel boxes={monthlyBoxes} upcoming={upcomingBoxes} month={monthlyMonth} onRefresh={fetchData} orders={orders} onSelectOrder={setSelectedOrder} />
            ) : activeTab === 'abandoned' ? (
              <AbandonedCartsPanel carts={abandonedCarts} onRefresh={fetchData} />
            ) : activeTab === 'deleted' ? (
              <table className="w-full text-sm" data-testid="deleted-subs-table">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-100">
                    {['Sub ID', 'Customer', 'Pet', 'Plan', 'Price', 'Status', 'Deleted', ''].map(h => (
                      <th key={h} className="px-4 py-3 font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {deletedSubs.length === 0 ? (
                    <tr><td colSpan={8} className="text-center py-12 text-gray-400">No deleted or cancelled subscriptions</td></tr>
                  ) : deletedSubs.map(sub => (
                    <tr key={sub.subscription_id} className="border-b border-gray-50 hover:bg-gray-50/50 cursor-pointer"
                      onClick={() => setSelectedSub(sub)} data-testid={`deleted-row-${sub.subscription_id}`}>
                      <td className="px-4 py-3 font-mono text-xs text-gray-600">{sub.subscription_id?.slice(0, 16)}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{sub.user_name || '-'}</div>
                        <div className="text-xs text-gray-500">{sub.user_email || '-'}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{sub.pet_name || '-'}</div>
                        <div className="text-xs text-gray-500 capitalize">{sub.pet_type}</div>
                      </td>
                      <td className="px-4 py-3 capitalize">{sub.plan_type}</td>
                      <td className="px-4 py-3 font-semibold">{sub.currency === 'RON' ? '' : '\u20AC'}{sub.price?.toFixed(2)}{sub.currency === 'RON' ? ' lei' : ''}</td>
                      <td className="px-4 py-3"><StatusBadge status={sub.status} /></td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{sub.deleted_at ? new Date(sub.deleted_at).toLocaleDateString() : sub.cancelled_at ? new Date(sub.cancelled_at).toLocaleDateString() : '-'}</td>
                      <td className="px-4 py-3"><ChevronRight className="w-4 h-4 text-gray-400" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : activeTab === 'orders' ? (
              <table className="w-full text-sm" data-testid="orders-table">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-100">
                    <th className="px-2 py-3 w-10"><input type="checkbox" onChange={() => toggleSelectAll(filteredOrders.map(o => o.order_id))} checked={filteredOrders.length > 0 && filteredOrders.every(o => selectedIds.has(o.order_id))} className="rounded" /></th>
                    {['Order ID', 'Customer', 'Pet', 'Type', 'Amount', 'Payment', 'Status', 'Date', ''].map(h => (
                      <th key={h} className="px-4 py-3 font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.length === 0 ? (
                    <tr><td colSpan={10} className="text-center py-12 text-gray-400">No orders found</td></tr>
                  ) : filteredOrders.map(order => (
                    <tr key={order.order_id} className={`border-b border-gray-50 hover:bg-gray-50/50 cursor-pointer ${selectedIds.has(order.order_id) ? 'bg-brand-orange/5' : ''}`}
                      data-testid={`order-row-${order.order_id}`}>
                      <td className="px-2 py-3" onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={selectedIds.has(order.order_id)} onChange={() => toggleSelect(order.order_id)} className="rounded" />
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-600" onClick={() => setSelectedOrder(order)}>{order.order_id?.slice(0, 16)}</td>
                      <td className="px-4 py-3" onClick={() => setSelectedOrder(order)}>
                        <div className="font-medium text-gray-900">{order.customer_name}</div>
                        <div className="text-xs text-gray-500">{order.email}</div>
                      </td>
                      <td className="px-4 py-3" onClick={() => setSelectedOrder(order)}>
                        <div className="font-medium">{order.pet_name}</div>
                        <div className="text-xs text-gray-500">{order.breed || '-'}</div>
                      </td>
                      <td className="px-4 py-3 capitalize" onClick={() => setSelectedOrder(order)}>{order.pet_type}{order.wants_upgrade && <span className="ml-1 text-xs text-brand-orange font-semibold">+UP</span>}</td>
                      <td className="px-4 py-3 font-semibold" onClick={() => setSelectedOrder(order)}>{order.currency === 'RON' ? '' : '\u20AC'}{order.total_price?.toFixed(2)}{order.currency === 'RON' ? ' lei' : ''}</td>
                      <td className="px-4 py-3" onClick={() => setSelectedOrder(order)}>
                        {order.payment_method === 'ramburs' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold">
                            <Truck className="w-3 h-3" /> COD
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                            <CreditCard className="w-3 h-3" /> Card
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3" onClick={() => setSelectedOrder(order)}><StatusBadge status={order.status} /></td>
                      <td className="px-4 py-3 text-gray-500 text-xs" onClick={() => setSelectedOrder(order)}>{order.created_at ? new Date(order.created_at).toLocaleDateString() : '-'}</td>
                      <td className="px-4 py-3" onClick={() => setSelectedOrder(order)}><ChevronRight className="w-4 h-4 text-gray-400" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="w-full text-sm" data-testid="subscriptions-table">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-100">
                    <th className="px-2 py-3 w-10"><input type="checkbox" onChange={() => toggleSelectAll(filteredSubs.map(s => s.subscription_id))} checked={filteredSubs.length > 0 && filteredSubs.every(s => selectedIds.has(s.subscription_id))} className="rounded" /></th>
                    {['Sub ID', 'Customer', 'Pet', 'Plan', 'Price', 'Status', 'Start', ''].map(h => (
                      <th key={h} className="px-4 py-3 font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredSubs.length === 0 ? (
                    <tr><td colSpan={9} className="text-center py-12 text-gray-400">No subscriptions found</td></tr>
                  ) : filteredSubs.map(sub => (
                    <tr key={sub.subscription_id} className={`border-b border-gray-50 hover:bg-gray-50/50 cursor-pointer ${selectedIds.has(sub.subscription_id) ? 'bg-brand-orange/5' : ''}`}
                      data-testid={`sub-row-${sub.subscription_id}`}>
                      <td className="px-2 py-3" onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={selectedIds.has(sub.subscription_id)} onChange={() => toggleSelect(sub.subscription_id)} className="rounded" />
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-600" onClick={() => setSelectedSub(sub)}>{sub.subscription_id?.slice(0, 16)}</td>
                      <td className="px-4 py-3" onClick={() => setSelectedSub(sub)}>
                        <div className="font-medium text-gray-900">{sub.user_name || '-'}</div>
                        <div className="text-xs text-gray-500">{sub.user_email || '-'}</div>
                      </td>
                      <td className="px-4 py-3" onClick={() => setSelectedSub(sub)}>
                        <div className="font-medium">{sub.pet_name || '-'}</div>
                        <div className="text-xs text-gray-500 capitalize">{sub.pet_type}</div>
                      </td>
                      <td className="px-4 py-3 capitalize" onClick={() => setSelectedSub(sub)}>{sub.plan_type}</td>
                      <td className="px-4 py-3 font-semibold" onClick={() => setSelectedSub(sub)}>{sub.currency === 'RON' ? '' : '\u20AC'}{sub.price?.toFixed(2)}{sub.currency === 'RON' ? ' lei' : ''}</td>
                      <td className="px-4 py-3" onClick={() => setSelectedSub(sub)}><StatusBadge status={sub.status} /></td>
                      <td className="px-4 py-3 text-gray-500 text-xs" onClick={() => setSelectedSub(sub)}>{sub.start_date ? new Date(sub.start_date).toLocaleDateString() : '-'}</td>
                      <td className="px-4 py-3" onClick={() => setSelectedSub(sub)}><ChevronRight className="w-4 h-4 text-gray-400" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="p-4 border-t border-gray-100 text-sm text-gray-500">
            Showing {activeTab === 'orders' ? filteredOrders.length : activeTab === 'monthly' ? monthlyBoxes.length : activeTab === 'abandoned' ? abandonedCarts.length : activeTab === 'deleted' ? deletedSubs.length : filteredSubs.length} {activeTab === 'deleted' ? 'deleted/cancelled' : activeTab}
            {activeTab === 'orders' && <span className="text-gray-400 ml-2">— Click a row to view details</span>}
            {activeTab === 'subscriptions' && <span className="text-gray-400 ml-2">— Click a row to manage</span>}
            {activeTab === 'deleted' && <span className="text-gray-400 ml-2">— Click a row to restore</span>}
            {activeTab === 'monthly' && <span className="text-gray-400 ml-2">— Attach tracking codes to ship boxes</span>}
          </div>
        </div>
      </main>

      {/* Modals */}
      {selectedOrder && (
        <OrderDetailModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onRefresh={() => { fetchData(); setSelectedOrder(null); }}
        />
      )}
      {selectedSub && (
        <SubDetailModal
          sub={selectedSub}
          onClose={() => setSelectedSub(null)}
          onRefresh={() => { fetchData(); setSelectedSub(null); }}
          onViewOrder={(orderId) => {
            const order = orders.find(o => o.order_id === orderId);
            if (order) {
              setSelectedSub(null);
              setActiveTab('orders');
              setSelectedOrder(order);
            }
          }}
        />
      )}
      {showCreateSub && (
        <CreateSubModal
          onClose={() => setShowCreateSub(false)}
          onRefresh={fetchData}
        />
      )}
      {showDuplicates && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowDuplicates(false)} />
          <div className="relative bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto shadow-2xl" data-testid="duplicates-modal">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h2 className="text-lg font-bold text-gray-900">Duplicate Checker</h2>
              <button onClick={() => setShowDuplicates(false)} className="p-2 text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6">
              {loadingDupes ? (
                <div className="flex items-center justify-center py-12"><RefreshCw className="w-6 h-6 text-brand-orange animate-spin" /></div>
              ) : duplicates ? (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                      <Mail className="w-4 h-4" /> Duplicate Emails
                      <span className="text-xs font-normal px-2 py-0.5 bg-red-100 text-red-700 rounded-full">{duplicates.email_count}</span>
                    </h3>
                    {duplicates.email_count === 0 ? (
                      <p className="text-sm text-gray-400">No duplicate emails found</p>
                    ) : Object.entries(duplicates.duplicate_emails).map(([email, items]) => (
                      <div key={email} className="mb-3 bg-gray-50 rounded-xl p-3">
                        <p className="text-sm font-semibold text-gray-800 mb-2">{email} <span className="text-xs text-gray-400">({items.length} entries)</span></p>
                        <div className="space-y-1">
                          {items.map((item, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs text-gray-600">
                              <span className={`px-1.5 py-0.5 rounded font-medium ${item.type === 'order' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>{item.type}</span>
                              <span className="font-mono">{item.id?.slice(0, 20)}</span>
                              <span>{item.name}</span>
                              <StatusBadge status={item.status} />
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                      <Phone className="w-4 h-4" /> Duplicate Phone Numbers
                      <span className="text-xs font-normal px-2 py-0.5 bg-red-100 text-red-700 rounded-full">{duplicates.phone_count}</span>
                    </h3>
                    {duplicates.phone_count === 0 ? (
                      <p className="text-sm text-gray-400">No duplicate phone numbers found</p>
                    ) : Object.entries(duplicates.duplicate_phones).map(([phone, items]) => (
                      <div key={phone} className="mb-3 bg-gray-50 rounded-xl p-3">
                        <p className="text-sm font-semibold text-gray-800 mb-2">{phone} <span className="text-xs text-gray-400">({items.length} entries)</span></p>
                        <div className="space-y-1">
                          {items.map((item, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs text-gray-600">
                              <span className={`px-1.5 py-0.5 rounded font-medium ${item.type === 'order' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>{item.type}</span>
                              <span className="font-mono">{item.id?.slice(0, 20)}</span>
                              <span>{item.name || item.email}</span>
                              <StatusBadge status={item.status} />
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-center text-gray-400 py-8">Failed to load duplicates</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
